// Signage → A/B testing (/signage/experimentos). Compara dos variantes de plantilla
// alternándolas DÍA A DÍA (switchback) sobre un grupo de pantallas, y mide impresiones
// (proof-of-play) y ventas del local por variante. El ganador es orientativo
// (correlación con control temporal, no causalidad garantizada).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, FlaskConical, Pencil, Play, Plus, Square, Trash2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, fieldCls } from '@/features/pos/shared';
import { RefRow, sigInsert, sigList, sigRpc, sigUpdate } from './shared';

interface Experimento {
  id: string; nombre: string; grupo_id: string; plantilla_a: string; plantilla_b: string;
  fecha_inicio: string; fecha_fin: string; estado: 'borrador' | 'activo' | 'finalizado';
}
interface VarResultado { plantilla: string; dias: number; impresiones: number; unidades: number; monto: number; monto_dia: number }
interface Resultado { id: string; nombre: string; estado: string; fecha_inicio: string; fecha_fin: string; a: VarResultado; b: VarResultado; ganador: 'A' | 'B' | null }

const ESTADOS: Record<Experimento['estado'], { l: string; c: string }> = {
  borrador: { l: 'Borrador', c: 'bg-muted text-muted-foreground' },
  activo: { l: 'Activo', c: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  finalizado: { l: 'Finalizado', c: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};
const hoy = () => new Date().toISOString().slice(0, 10);
const enDias = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const money = (n: number) => `$${Number(n).toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function Experimentos() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Experimento> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [verId, setVerId] = useState<string | null>(null);

  const { data: exps = [] } = useQuery<Experimento[]>({ queryKey: ['sig-exps'], queryFn: () => sigList<Experimento>('experimento?select=*&deleted_at=is.null&order=created_at.desc') });
  const { data: grupos = [] } = useQuery<RefRow[]>({ queryKey: ['sig-grupos'], queryFn: () => sigList<RefRow>('grupo?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: plantillas = [] } = useQuery<RefRow[]>({ queryKey: ['sig-plantillas'], queryFn: () => sigList<RefRow>('plantilla?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: resultado } = useQuery<Resultado>({ queryKey: ['sig-exp-res', verId], enabled: !!verId, staleTime: 0, queryFn: () => sigRpc<Resultado>('sig_experimento_resultado', { _id: verId }) });

  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-exps'] });
  const nom = (id: string, arr: RefRow[]) => arr.find((r) => r.id === id)?.nombre ?? '—';

  const guardar = async () => {
    if (!edit?.nombre || !edit.grupo_id || !edit.plantilla_a || !edit.plantilla_b) { toast.error('Completa nombre, grupo y ambas plantillas'); return; }
    if (edit.plantilla_a === edit.plantilla_b) { toast.error('Las dos variantes deben ser plantillas distintas'); return; }
    setGuardando(true);
    try {
      const body = {
        nombre: edit.nombre, grupo_id: edit.grupo_id, plantilla_a: edit.plantilla_a, plantilla_b: edit.plantilla_b,
        fecha_inicio: edit.fecha_inicio || hoy(), fecha_fin: edit.fecha_fin || enDias(14),
      };
      if (edit.id) await sigUpdate('experimento', edit.id, body); else await sigInsert('experimento', body);
      setEdit(null); invalidar(); toast.success('Experimento guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const setEstado = (x: Experimento, estado: string) => sigUpdate('experimento', x.id, { estado }).then(invalidar).then(() => toast.success('Estado actualizado')).catch((e) => toast.error(getErrorMessage(e)));
  const eliminar = (x: Experimento) => sigUpdate('experimento', x.id, { deleted_at: new Date().toISOString() }).then(invalidar).then(() => toast.success('Eliminado')).catch((e) => toast.error(getErrorMessage(e)));

  const sinGrupos = !grupos.length;

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FlaskConical className="w-5 h-5" /> A/B testing</h1>
        <Button className="ml-auto" disabled={sinGrupos} onClick={() => setEdit({ fecha_inicio: hoy(), fecha_fin: enDias(14) })}><Plus className="w-4 h-4 mr-1" /> Nuevo experimento</Button>
      </div>
      <p className="text-sm text-muted-foreground">Compara dos plantillas <strong>alternándolas día a día</strong> (switchback) sobre un grupo de pantallas. Medimos impresiones y ventas del local por variante. El ganador es <strong>orientativo</strong> (control temporal, no causalidad garantizada).</p>
      {sinGrupos && <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">Primero crea un <strong>grupo de pantallas</strong> (Signage → Grupos): el experimento corre sobre un grupo.</div>}

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Experimento</th>
            <th className="py-2 px-3 text-left">Grupo</th><th className="py-2 px-3 text-left">A vs B</th>
            <th className="py-2 px-3 text-left">Vigencia</th><th className="py-2 px-3 text-center">Estado</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {exps.map((x, i) => (
              <tr key={x.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3 font-medium">{x.nombre}</td>
                <td className="py-2 px-3 text-muted-foreground">{nom(x.grupo_id, grupos)}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{nom(x.plantilla_a, plantillas)} <span className="opacity-50">vs</span> {nom(x.plantilla_b, plantillas)}</td>
                <td className="py-2 px-3 text-muted-foreground tabular-nums">{x.fecha_inicio} → {x.fecha_fin}</td>
                <td className="py-2 px-3 text-center"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ESTADOS[x.estado].c}`}>{ESTADOS[x.estado].l}</span></td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Ver resultado" onClick={() => setVerId(x.id)}><BarChart3 className="w-4 h-4" /></button>
                  {x.estado === 'borrador' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Activar" onClick={() => void setEstado(x, 'activo')}><Play className="w-4 h-4 text-emerald-600" /></button>}
                  {x.estado === 'activo' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Finalizar" onClick={() => void setEstado(x, 'finalizado')}><Square className="w-4 h-4 text-amber-600" /></button>}
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(x)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => void eliminar(x)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!exps.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin experimentos.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? 'Editar experimento' : 'Nuevo experimento'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
            <Campo label="Grupo de pantallas">
              <select className={fieldCls} value={edit.grupo_id ?? ''} onChange={(e) => setEdit({ ...edit, grupo_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Variante A">
                <select className={fieldCls} value={edit.plantilla_a ?? ''} onChange={(e) => setEdit({ ...edit, plantilla_a: e.target.value })}>
                  <option value="">— Plantilla —</option>
                  {plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Variante B">
                <select className={fieldCls} value={edit.plantilla_b ?? ''} onChange={(e) => setEdit({ ...edit, plantilla_b: e.target.value })}>
                  <option value="">— Plantilla —</option>
                  {plantillas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Desde"><input className={fieldCls} type="date" value={edit.fecha_inicio ?? hoy()} onChange={(e) => setEdit({ ...edit, fecha_inicio: e.target.value })} /></Campo>
              <Campo label="Hasta"><input className={fieldCls} type="date" value={edit.fecha_fin ?? enDias(14)} onChange={(e) => setEdit({ ...edit, fecha_fin: e.target.value })} /></Campo>
            </div>
            <p className="text-xs text-muted-foreground">Al activarlo, las pantallas del grupo muestran la Variante A los días pares desde el inicio y la B los impares. Ambas plantillas deben estar <strong>publicadas</strong>.</p>
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {verId && resultado && (
        <Modal title={`Resultado — ${resultado.nombre}`} onClose={() => setVerId(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {(['a', 'b'] as const).map((k) => {
                const v = resultado[k]; const gana = resultado.ganador === k.toUpperCase();
                return (
                  <div key={k} className={`rounded-lg border p-3 ${gana ? 'ring-2 ring-emerald-500' : ''}`}>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">Variante {k.toUpperCase()} {gana && <Trophy className="w-3.5 h-3.5 text-emerald-600" />}</div>
                    <div className="font-medium truncate" title={v.plantilla}>{v.plantilla}</div>
                    <dl className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between"><dt className="text-muted-foreground">Días</dt><dd className="tabular-nums">{v.dias}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Impresiones</dt><dd className="tabular-nums">{v.impresiones.toLocaleString('es')}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Unidades</dt><dd className="tabular-nums">{v.unidades.toLocaleString('es')}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Ventas</dt><dd className="tabular-nums">{money(v.monto)}</dd></div>
                      <div className="flex justify-between font-semibold border-t pt-1"><dt>Ventas / día</dt><dd className="tabular-nums">{money(v.monto_dia)}</dd></div>
                    </dl>
                  </div>
                );
              })}
            </div>
            <div className="text-sm">
              {resultado.ganador ? <p>Gana la <strong>Variante {resultado.ganador}</strong> por ventas/día en el período medido.</p> : <p className="text-muted-foreground">Aún sin diferencia de ventas/día (o sin datos suficientes).</p>}
            </div>
            <p className="text-xs text-muted-foreground border-t pt-2">Lectura orientativa: el switchback controla el local alternando por día, pero las ventas dependen de muchos factores. Deja correr el experimento varios días antes de decidir.</p>
            <div className="flex justify-end"><Button variant="ghost" onClick={() => setVerId(null)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
